import pluralize from 'pluralize';
import { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { type Clip } from '@air/api/types';
import { ChevronDownIcon, XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Popover } from '@air/primitive-popover';

import { ClipThumbnail } from '~/components/Shared/ClipList/ClipItem/ClipThumbail';
import { useWorkspaceAgentClipsForClipIds } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentClipsForClipIds';
import { centralizedBoardTitleSelector } from '~/store/centralizedBoard/selectors';
import { ClipIconSize } from '~/types/clip-icon-size';

const THUMB_SIZE = 20;
const MAX_PEEK_THUMBS = 3;
const EDIT_CLIPS_TAGS_AGENT_SCOPE_HEADER_ID = 'edit-clips-tags-agent-scope-header';

export type EditClipsTagsModalAgentScopeDropdownProps = {
  clipIds: Clip['id'][];
  onRemoveClipFromSelection: (clipId: Clip['id']) => void;
  scopeLabel?: string;
};

type ClipScopeRowProps = {
  clip: Clip;
  onRemoveClipFromSelection: (clipId: Clip['id']) => void;
};

const ClipScopeRow = memo(({ clip, onRemoveClipFromSelection }: ClipScopeRowProps) => {
  const title = clip.title || clip.importedName || 'Untitled';

  const handleRemove = useCallback(() => {
    onRemoveClipFromSelection(clip.id);
  }, [clip.id, onRemoveClipFromSelection]);

  return (
    <div className="flex w-full items-center gap-2 rounded-4 px-1 py-1 transition-colors hover:ads:bg-hover-subtle hover:bg-gray-3">
      <span className="relative size-7 shrink-0 overflow-hidden rounded-4 ads:bg-subtle bg-gray-4">
        <ClipThumbnail
          className="rounded-4"
          description={clip.description}
          ext={clip.ext}
          height={28}
          hideText
          iconSize={ClipIconSize.small}
          importedName={clip.importedName}
          size="small"
          src={clip.assets.image}
          status={clip.status}
          type={clip.type}
          width={28}
        />
      </span>
      <span className="min-w-0 flex-1 truncate font-medium ads:text-primary text-12 text-gray-12">{title}</span>
      <IconButton
        adsIcon
        appearance="ghost"
        className="shrink-0"
        color="gray"
        icon={XIcon}
        label={`Remove ${title} from selection`}
        onClick={handleRemove}
        size="small"
      />
    </div>
  );
});

ClipScopeRow.displayName = 'ClipScopeRow';

type ScopePeekThumbsProps = {
  clips: Clip[];
};

const ScopePeekThumbs = memo(({ clips }: ScopePeekThumbsProps) => {
  const peekClips = clips.slice(0, MAX_PEEK_THUMBS);

  if (peekClips.length === 0) {
    return null;
  }

  return (
    <span aria-hidden className="flex shrink-0 -space-x-1.5">
      {peekClips.map((clip) => (
        <span
          className="relative size-5 shrink-0 overflow-hidden rounded-4 ads:bg-subtle bg-gray-4 ads:ring-ads-gray-1 ring-1 ring-gray-1"
          key={clip.id}
        >
          <ClipThumbnail
            className="rounded-4"
            description={clip.description}
            ext={clip.ext}
            height={THUMB_SIZE}
            hideText
            iconSize={ClipIconSize.small}
            importedName={clip.importedName}
            size="small"
            src={clip.assets.image}
            status={clip.status}
            type={clip.type}
            width={THUMB_SIZE}
          />
        </span>
      ))}
    </span>
  );
});

ScopePeekThumbs.displayName = 'ScopePeekThumbs';

type ScopeSummaryTriggerProps = {
  clipCount: number;
  clips: Clip[];
  scopeLabel?: string;
  showChevron: boolean;
};

const ScopeSummaryTrigger = memo(({ clipCount, clips, scopeLabel, showChevron }: ScopeSummaryTriggerProps) => {
  const countLabel = `${clipCount.toLocaleString()} ${pluralize('item', clipCount)}`;

  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <ScopePeekThumbs clips={clips} />
      <span className="min-w-0 flex-1 truncate font-medium ads:text-primary text-12 text-gray-12">
        {countLabel}
        {scopeLabel ? ` · ${scopeLabel}` : ''}
      </span>
      {showChevron ? (
        <ChevronDownIcon className="size-3.5 shrink-0 ads:text-icon-subtle text-gray-9" size="sm" />
      ) : null}
    </span>
  );
});

ScopeSummaryTrigger.displayName = 'ScopeSummaryTrigger';

export const EditClipsTagsModalAgentScopeDropdown = memo(
  ({ clipIds, onRemoveClipFromSelection, scopeLabel }: EditClipsTagsModalAgentScopeDropdownProps) => {
    const boardTitle = useSelector(centralizedBoardTitleSelector);
    const clips = useWorkspaceAgentClipsForClipIds(clipIds);

    const boardScopeLabel = scopeLabel ?? boardTitle ?? undefined;
    const showDropdown = clipIds.length > 1;

    const summary = (
      <ScopeSummaryTrigger
        clipCount={clipIds.length}
        clips={clips}
        scopeLabel={boardScopeLabel}
        showChevron={showDropdown}
      />
    );

    if (!showDropdown) {
      return (
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-6 border ads:border-ads-gray-5 border-gray-5 ads:bg-base bg-gray-1 px-2 py-1.5 text-left">
          {summary}
        </div>
      );
    }

    const trigger = (
      <button
        className="flex min-w-0 flex-1 items-center gap-2 rounded-6 border ads:border-input border-gray-5 ads:bg-base bg-gray-1 px-2 py-1.5 text-left"
        type="button"
      >
        {summary}
      </button>
    );

    return (
      <Popover
        aria-labelledby={EDIT_CLIPS_TAGS_AGENT_SCOPE_HEADER_ID}
        className="flex max-h-52 w-[min(400px,calc(100vw-2rem))] flex-col overflow-hidden rounded-8 border ads:border-ads-gray-5 border-gray-5 bg-surface-1 p-0 shadow-popover"
        data-testid="EDIT_CLIPS_TAGS_AGENT_SCOPE_DROPDOWN"
        modal
        side="top"
        sideOffset={4}
        trigger={trigger}
      >
        <div
          className="shrink-0 ads:border-ads-gray-5 border-gray-5 border-b px-3 py-2.5 font-medium ads:text-primary text-14 text-gray-12"
          id={EDIT_CLIPS_TAGS_AGENT_SCOPE_HEADER_ID}
        >
          {`${clipIds.length} ${pluralize('item', clipIds.length)} selected`}
        </div>
        <div className="max-h-52 min-h-0 flex-1 overflow-y-auto px-1.5 py-1.5">
          <div className="flex flex-col gap-px">
            {clips.map((clip) => (
              <ClipScopeRow clip={clip} key={clip.id} onRemoveClipFromSelection={onRemoveClipFromSelection} />
            ))}
          </div>
        </div>
      </Popover>
    );
  },
);

EditClipsTagsModalAgentScopeDropdown.displayName = 'EditClipsTagsModalAgentScopeDropdown';
